import type { ProductModel } from '@ox/schema';

/**
 * True when the model has real color swatches (distinct hex), not just several SKUs.
 * Server `showColors: false` always hides; `true`/omit still requires distinct hexes.
 */
export function modelShowsColors(model: ProductModel | null | undefined): boolean {
  if (!model) return false;
  if (model.showColors === false) return false;
  const colors = model.colors ?? [];
  if (colors.length <= 1) return false;
  const hexes = new Set(
    colors.map((c) => (c.hex || '#888888').replace(/^#/, '').toLowerCase()),
  );
  return hexes.size > 1;
}

/**
 * True when sizes are meaningful (not a single synthetic Única).
 * Apparel S–XXL and mug 11 Oz / 15 Oz both qualify.
 */
export function modelShowsSizes(model: ProductModel | null | undefined): boolean {
  if (!model) return false;
  if (typeof model.showSizes === 'boolean') return model.showSizes;
  const sizes = model.sizes ?? [];
  if (sizes.length > 1) return true;
  if (sizes.length === 1) {
    const id = sizes[0]!.id.toLowerCase();
    return id !== 'u' && id !== 'unica' && id !== 'única';
  }
  return false;
}

export function modelSizeAxisLabel(model: ProductModel | null | undefined): string {
  const raw = model?.sizeAxisLabel?.trim();
  if (raw) return raw;
  return 'Talla';
}

export function modelColorAxisLabel(model: ProductModel | null | undefined): string {
  const raw = model?.colorAxisLabel?.trim();
  if (raw) return raw;
  return 'Color';
}

/** Pluralize axis for “disponibles” copy (Talla→Tallas, Tamaño→Tamaños). */
export function pluralizeAxisLabel(label: string): string {
  const t = label.trim();
  if (!t) return 'Tallas';
  if (/s$/i.test(t)) return t;
  if (/ón$/i.test(t)) return t.replace(/ón$/i, 'ones');
  if (/z$/i.test(t)) return `${t}es`;
  return `${t}s`;
}

export function chooseSizeCtaLabel(model: ProductModel | null | undefined, fromCart: boolean): string {
  if (fromCart) return '🛒 Actualizar cesta';
  const axis = modelSizeAxisLabel(model).toLowerCase();
  if (!modelShowsSizes(model) && !modelShowsColors(model)) {
    return '🛒 Elegir cantidad';
  }
  if (!modelShowsSizes(model)) {
    return '🛒 Elegir cantidad';
  }
  return `🛒 Elegir ${axis} y cantidad`;
}

/** Prefer a color that has purchasable variants (Mis Diseños → wrong model/color). */
export function colorKeyWithVariants(
  model: ProductModel | null | undefined,
  preferred?: string | null,
): string {
  if (!model) return preferred?.trim() || 'negro';
  const pref = preferred?.trim() ?? '';
  // Custom garment hex (PDP picker) — keep for canvas; variants use purchasableColorKey.
  if (pref && isFreeHexColorKey(pref)) {
    return pref;
  }
  if (pref && model.colors.some((c) => c.key === pref)) {
    return pref;
  }
  if (pref && model.variants.some((v) => v.colorKey === pref)) {
    return pref;
  }
  for (const c of model.colors) {
    if (model.variants.some((v) => v.colorKey === c.key)) {
      return c.key;
    }
  }
  return model.colors[0]?.key || pref || 'negro';
}

/** True when colorKey is a free hex from the garment color picker (not a catalog SKU key). */
export function isFreeHexColorKey(key: string | null | undefined): boolean {
  if (!key) return false;
  const t = key.trim().replace(/^#/, '');
  return /^[0-9a-fA-F]{6}$/i.test(t);
}

/** `#rrggbb` / `rrggbb` → `rrggbb`, or empty. */
export function normalizeColorHex(raw: string | null | undefined): string {
  if (!raw) return '';
  const t = raw.trim().replace(/^#/, '').toLowerCase();
  return /^[0-9a-f]{6}$/.test(t) ? t : '';
}

function hexDistanceSq(a: string, b: string): number {
  const ar = parseInt(a.slice(0, 2), 16);
  const ag = parseInt(a.slice(2, 4), 16);
  const ab = parseInt(a.slice(4, 6), 16);
  const br = parseInt(b.slice(0, 2), 16);
  const bg = parseInt(b.slice(2, 4), 16);
  const bb = parseInt(b.slice(4, 6), 16);
  return (ar - br) ** 2 + (ag - bg) ** 2 + (ab - bb) ** 2;
}

/**
 * Catalog color that can be purchased for this hex.
 * Exact hex wins; otherwise the nearest swatch that has variants.
 * Avoids buying the first SKU in the model (often an unrelated color).
 */
export function catalogColorKeyForHex(
  model: ProductModel | null | undefined,
  hex: string | null | undefined,
): string {
  if (!model) return '';
  const want = normalizeColorHex(hex);
  if (!want) return '';
  let exact = '';
  let bestKey = '';
  let bestDist = Number.POSITIVE_INFINITY;
  for (const c of model.colors) {
    const h = normalizeColorHex(c.hex);
    if (!h) continue;
    if (!model.variants.some((v) => v.colorKey === c.key)) continue;
    if (h === want) {
      exact = c.key;
      break;
    }
    const dist = hexDistanceSq(want, h);
    if (dist < bestDist) {
      bestDist = dist;
      bestKey = c.key;
    }
  }
  return exact || bestKey;
}

/**
 * Color key used to pick Woo variants for quote/cart.
 * Custom garment hex has no variants — fall back to first purchasable without changing canvas.
 * Catalog colors keyed by hex still count as purchasable when they have variants.
 */
export function purchasableColorKey(
  model: ProductModel | null | undefined,
  preferred?: string | null,
  colorHex?: string | null,
): string {
  if (!model) return preferred?.trim() || 'negro';
  const pref = preferred?.trim() ?? '';
  if (pref && model.variants.some((v) => v.colorKey === pref)) {
    return pref;
  }
  // Picker / PDP sends a bare hex (`0c0c0c`) that is not the catalog key (`negro`).
  // Match the garment by hex so the Woo line is that color, not the first SKU.
  const fromHex =
    catalogColorKeyForHex(model, isFreeHexColorKey(pref) ? pref : '') ||
    catalogColorKeyForHex(model, colorHex);
  if (fromHex) return fromHex;
  for (const c of model.colors) {
    // Skip synthetic garment swatches that were injected into the catalog list.
    const isSynthetic =
      isFreeHexColorKey(c.key) && !model.variants.some((v) => v.colorKey === c.key);
    if (isSynthetic) continue;
    if (model.variants.some((v) => v.colorKey === c.key)) {
      return c.key;
    }
  }
  return model.variants[0]?.colorKey || model.colors[0]?.key || pref || 'negro';
}
